import { useState, useMemo } from 'react'
import { extendedColorPalette, color, FormatUtils, TZABrandGuidelines } from '@my/util'
import { YStack, useWindowDimensions, StackedBarChart, DonutChart, SimpleText, FinancialTable, BasicTableControlled, XStack } from '@my/ui'
import { ToggleButtonGroup } from '../../../components/index'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'
import { FinancialCard } from './financialCard'
import _ from 'lodash'

interface DataItem {
  x: string // month (e.g., "November", "December")
  y: number // amount (converted to number)
}

type WorthDataItem = {
  x: string
  y: number
  label: string
}

type OriginalData = {
  totalNetWorth: string
  values: Array<{
    key: string
    totalValue: string
    percentage: string
  }>
}

const generateLegendData = (categories: string[], colorPalette: string[]) => {
  return categories.map((category, index) => ({
    name: category,
    fill: colorPalette[index % colorPalette.length], // Loop through the palette if there are more categories than colors
  }))
}

const transformDataForBarChart = (
  data: { month: string; data: { category: string; amount: string }[] }[]
) => {
  // Extract unique categories from the data
  const categories = Array.from(
    new Set(data?.flatMap((month) => month?.data?.map((item) => item.category)) || [])
  )

  // Initialize empty arrays for each category, with the correct type
  const barChart: DataItem[][] = categories.map ? categories.map(() => ([])) : []

  // Loop through each month's data and populate the transformedData
  (data || []).forEach((month) => {
    categories.forEach((category, index) => {
      const categoryData = month.data.find((d) => d.category === category)

      // Push the corresponding y-value or 0 if the category isn't in this month
      barChart[index].push({
        x: month.month, // x = month (e.g., "November" or "December")
        y: categoryData ? parseFloat(categoryData.amount) : 0, // Convert amount from string to number
      })
    })
  })

  return { barChart, categories }
}

const transformDonutChartDataOld = (
  data: OriginalData
): { donutChart: WorthDataItem[]; keys: string[] } => {
  // Filter out items where the percentage is 0
  const filteredValues = data?.values?.filter((item) => parseFloat(item.percentage) !== 0) || [];

  const donutChart = filteredValues.map((item) => ({
    x: `${item.percentage}%`,
    y: parseFloat(item.percentage),
    label: `${parseFloat(item.percentage).toFixed(1)}%`,
  }))

  const keys = filteredValues.map((item) => item.key)

  return { donutChart, keys }
}

const transformDonutChartData = (
  monthlyCashFlows: any
): { donutChart: WorthDataItem[]; keys: string[] } => {
  // Filter out items where the percentage is 0
  //const filteredValues = data?.values?.filter((item) => parseFloat(item.percentage) !== 0) || [];
  const genDatum = (val: string) => {
    const num = parseFloat(val);
    return {
      x: `$${val}`,
      y: Math.abs(num),
      label: FormatUtils.currency.cashFormat(num), //`${num < 0 ? '-' : ''}$${Math.abs(num)}`,
      raw: num
    }
  };

  const NetCashFlow = genDatum(monthlyCashFlows?.MonthlyNetCashFlow || '0');
  const chartData = {
    Spending: genDatum(monthlyCashFlows?.Operating?.Expenses?.total || '0'),
    'Investing/Saving/Debt Payments': genDatum((monthlyCashFlows?.['Investing/Saving/Debt Payments'] || monthlyCashFlows?.['Investing/Saving'])?.total || '0'),
    [NetCashFlow.raw >= 0 ? 'Surplus' : 'Deficit']: NetCashFlow,
  };
  const donutChart = Object.values(chartData);

  const keys = Object.keys(chartData);

  return { donutChart, keys }
}

const columnHelper = createColumnHelper();

export const MonthlyIncomeSpendingDonutChart: React.FC<{monthlyCashFlows: any}> = ({monthlyCashFlows}) => {
  const { donutChart, keys } = useMemo(
    () => transformDonutChartData(monthlyCashFlows),
    [monthlyCashFlows]
  );
  
  // Generate legend data based on categories and color palette
  const donutChartLegends = generateLegendData(keys, extendedColorPalette);
  //console.log({data: donutChart, legendData: donutChartLegends, colorScale: extendedColorPalette})
  return <YStack
        /*padding={20}
        borderWidth={1}
        borderRadius={10}
        borderColor={'rgba(108, 117, 125, 0.44)'}*/
      ><DonutChart
    data={donutChart}
    legendData={donutChartLegends}
    colorScale={extendedColorPalette}
  /></YStack>;
}

export const NetWorthDonutChart: React.FC<{netWorthChartData: {
    "Assets": {
        "val": number,
        "fmt": string
    },
    "Liabilities": {
        "val": number,
        "fmt": string
    }
}}> = ({netWorthChartData}) => {
  
  const { donutChart, keys } = useMemo(
    () => {
      
        const keys = ['Liabilities', 'Assets'] as const;
        const donutChart = netWorthChartData ? keys.map(key => ({
            x: netWorthChartData[key].fmt,
            y: netWorthChartData[key].val,
            label: FormatUtils.currency.cashFormat(netWorthChartData[key].val, { decimalPlaces: 0}), // netWorthChartData[key].fmt,
            raw: netWorthChartData[key].val,
        })) : [];
        return { donutChart, keys: keys as unknown as string[] };
    },
    [netWorthChartData]
  );
  
  const chartColors = [
    TZABrandGuidelines.colorsMap.secondaryMustard, 
    TZABrandGuidelines.colorsMap.secondaryBrightBlue,
    ...extendedColorPalette
  ];

  // Generate legend data based on categories and color palette
  const donutChartLegends = generateLegendData(keys, chartColors);
  
  return <YStack
        /*padding={20}
        borderWidth={1}
        borderRadius={10}
        borderColor={'rgba(108, 117, 125, 0.44)'}*/
      ><DonutChart
    data={donutChart}
    legendData={donutChartLegends}
    colorScale={chartColors}
  /></YStack>;
}

export const SnapShotChart = (props: { donutChartData, barChartData, total, monthlyCashFlows, netWorthChartData }) => {
  const { donutChartData, monthlyCashFlows, netWorthChartData, ...restProps } = props;
  const barChartData = restProps.barChartData || [];
  const total = restProps.total || 0;
  //const { width } = useWindowDimensions()
  const isLargeScreen = true; //width > 600

  // Manage the selected time period
  const [activePeriod, setActivePeriod] = useState('3 Months')

  // Determine whether the toggle button should be displayed
  const showToggleButton = barChartData.length > 3

  // Filter the months based on the selected time period
  const getFilteredMonths = () => {
    const months = barChartData.map((item) => item.month)
    let filteredMonths: string[]

    switch (activePeriod) {
      case '3 Months':
        filteredMonths = months.slice(-3)
        break
      case '6 Months':
        filteredMonths = months.slice(-6)
        break
      case '1 Year':
      default:
        filteredMonths = months
        break
    }

    return filteredMonths
  }

  // Filter the bar chart data to only include the selected months
  const filteredBarChartData = barChartData.filter((item) =>
    getFilteredMonths().includes(item.month)
  )

  const monthlyCashFlowTableData = [...Object.entries(monthlyCashFlows.Operating), ...Object.entries(monthlyCashFlows['Investing/Saving/Debt Payments'] || monthlyCashFlows['Investing/Saving'])]
  .filter(([_, obj]) => typeof obj === 'object')
  .map((rowObj) => ({
    label: rowObj[0],
    value: (rowObj[1] as any).total
  }));
  return (
    <XStack flexDirection={isLargeScreen ? 'row' : 'column'} flexWrap="wrap" gap={20} justifyContent='center'>
      <YStack>
        <YStack gap={10}>
          <SimpleText fontSize={14} lineHeight={29} fontWeight={'800'} color={'#555555'}>
            Net Worth
          </SimpleText>
          {/* <SimpleText fontSize={24} lineHeight={32} fontWeight={'800'} color={'#85C19D'}>
            ${donutChartData?.totalNetWorth}
          </SimpleText> */}
        </YStack>
        <NetWorthDonutChart netWorthChartData={netWorthChartData} />
      </YStack>
      <YStack>
        <YStack gap={10}>
          <SimpleText fontSize={14} lineHeight={29} fontWeight={'800'} color={'#555555'}>
            Cash Flow
          </SimpleText>
        </YStack>
        {/* <StackedBarChart
          horizontal={false}
          data={barChart}
          legendData={barChartLegends}
          stackColorScale={extendedColorPalette}
        /> */}
        
        <FinancialCard
          title={'Monthly Money Movement'}
          themeColor={'rgba(108, 117, 125, 0.44)'}
          details={monthlyCashFlowTableData}
          isExpanded={true}
          removeDescriptionLabel={true}
          rootStyle={{marginTop: '20%'}}
        />
        {/* <BasicTableControlled reactTable={monthlyCashflowTable} disableHeader={true} disableFooter={true} /> */}
      </YStack>
      <YStack>
        <SimpleText fontSize={14} lineHeight={29} fontWeight={'800'} color={'#555555'}>
          Spending Breakdown
        </SimpleText>
        {/* <ToggleButtonGroup active={worthActivePeriod} onChange={setWorthActivePeriod} /> */}

        <MonthlyIncomeSpendingDonutChart monthlyCashFlows={monthlyCashFlows} />
      </YStack>
    </XStack>
  )
}
